import { getGlobalData } from "../utils/api"
import Seo from "../components/elements/seo"
import Head from "next/head"
//import FaireDirect from "../components/elements/faire-direct"
import Link from "next/link"
import { BreadcrumbJsonLd, FAQPageJsonLd, LocalBusinessJsonLd } from "next-seo"

const WholesalePage = ({ global, metadata }) => {

    const metadataWithDefaults = {
        title: "Wholesale Rice Crispy Treats | Low MOQ, Fast Shipping",
        description: "Offer soft, crave worth crispy treats to your customers.",
        ...metadata,
    }
    const canonicalHref = metadataWithDefaults.canonicalURL

    return (<>
        <Head>
            <link rel="canonical" href={canonicalHref} />
        </Head>

        <div className="py-2" key="_storepage_2" id="_storefront_2">
            <Seo metadata={metadataWithDefaults}>
                <BreadcrumbJsonLd {...breadcrumbsJsonLd} />
                <LocalBusinessJsonLd {...offerCatalogJsonLd} />
                <FAQPageJsonLd {...faqJsonLd} />
            </Seo>
            <section className="prose max-w-3xl mx-auto p-6">
                <h1>Goldmallow for Wholesale</h1>
                <h2>Why We Created Goldmallow</h2>
                <p>Browned & Toasted started with a simple question:
                    What if the ingredients everyone uses were treated as flavor, not filler?
                    
                    Goldmallow is our answer — beginning with marshmallow, where sugar matters most, and expanding anywhere comfort desserts deserve more depth.
                </p>

                <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
                    <div className="rounded-xl border p-4">
                        <h2 className="text-lg font-semibold">Order Direct from Browned &amp; Toasted</h2>
                        <ul className="list-disc pl-5 mt-2">
                            <li>One case minimum on your first order</li>
                            <li>12-pack and 24-pack case options</li>
                            <li>Most flavors available; seasonal flavors rotate</li>
                            <li>Ship to US, Canada, and Mexico</li>
                        </ul>
                        <div className="mt-4 flex gap-3">
                            <Link href="/store" className="inline-block rounded-lg border px-4 py-2">Browse flavors</Link>
                            <a className="inline-block rounded-lg border px-4 py-2" href="mailto:hello@brownedandtoasted.com?subject=Interested%20in%20Wholesale" rel="nofollow noopener noreferrer" target="_blank">Email Us</a>
                        </div>
                    </div>

                    <div className="rounded-xl border p-4">
                        <h2 className="text-lg font-semibold">Prefer ordering on Faire?</h2>
                        <p className="mt-2">
                            If your team already uses Faire, you can reorder there to keep everything in one place. Pricing and availability match our wholesale program.
                        </p>
                        <div className="mt-4 flex gap-3">
                            {/* Replace with your Faire shop link */}
                            <iframe src="https://www.faire.com/embed/bw_mrbwh6ehjh" width="360" height="64" scrolling="no" className="m-auto border-0 block max-w-full w-[360px] h-16"></iframe>
                            {/* <a className="inline-block rounded-lg border px-4 py-2" href="https://brownedandtoasted.faire.com" rel="nofollow noopener noreferrer" target="_blank">
                Open our Faire shop
              </a> */}
                        </div>
                    </div>
                </div>

                <h2>Case Packs &amp; Minimums</h2>

                <p>
                    Our wholesale case sizes are <strong>12-pack</strong> and <strong>24-pack</strong>. For your first order, the minimum is a <strong>12-pack case.</strong>That&apos;s it!
                </p>

                <h2>Flavors for Wholesale</h2>
                <p>
                    Most of our flavors are available to wholesale customers year-round. Seasonal and limited flavors rotate in. Check the{" "}
                    <Link href="/store">current lineup</Link> or reach out and we’ll confirm availability for your order window.
                </p>

                <h2>Shipping &amp; Delivery</h2>
                <ul className="list-disc pl-5">
                    <li>Wholesale customers cover shipping costs (no free shipping on wholesale orders).</li>
                    <li>We ship within the U.S., Canada, and Mexico.</li>
                </ul>

                <h2>Great for Retail &amp; Events</h2>
                <p>
                    Our bars move fast in cafés, boutique groceries, gift shops, bookstores, and campus markets. They’re also a hit for corporate gifting and catered events.
                    If you’re planning a larger activation, see <Link href="/corporate-gifts">corporate gifts</Link> or <Link href="/catering">catering</Link>.
                </p>

                <h2 id="wholesale-faq">Wholesale FAQ</h2>

                <details>
                    <summary>Do you offer wholesale pricing on most flavors?</summary>
                    <div className="mt-2">
                        Yes—most flavors are available. Seasonal flavors rotate. We’ll confirm what’s in production for your timing.
                    </div>
                </details>
                <details>
                    <summary>What’s the first-order minimum?</summary>
                    <div className="mt-2">
                        One case: either a 12-pack SKUs or a 24-pack SKUs.
                    </div>
                </details>
                <details>
                    <summary>Who pays shipping?</summary>
                    <div className="mt-2">
                        Wholesale customers pay shipping. Free shipping promos don’t apply to wholesale orders.
                    </div>
                </details>
                <details>
                    <summary>Can I place orders on Faire?</summary>
                    <div className="mt-2">
                        Yes—order on Faire if that’s your normal workflow, or order direct from us. Same products either way.
                    </div>
                </details>
                <details>
                    <summary>Where do you ship?</summary>
                    <div className="mt-2">
                        U.S., Canada, and Mexico.
                    </div>
                </details>
            </section>

            {/* <section className="max-w-5xl mx-auto p-6">
                <div id="faire-wholesale-widget" className="min-h-[320px] border rounded-xl p-4">
                    <FaireDirect />
                </div>
            </section> */}
        </div>
    </>
    )
}

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "Do you offer wholesale marshmallow crispy bars?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. We make gourmet marshmallow crispy bars for wholesale in 12-pack and 24-pack case sizes. Most flavors are available for wholesale customers."
            }
        },
        {
            "@type": "Question",
            "name": "What’s the minimum order?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "For your first order, the minimum is a half-case. That’s 6 bars for 12-pack SKUs or 12 bars for 24-pack SKUs."
            }
        },
        {
            "@type": "Question",
            "name": "Can I order on Faire?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. If you already buy on Faire, you can order there. You can also order directly from us—whichever is easier for your team."
            }
        },
        {
            "@type": "Question",
            "name": "Where do you ship?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "We ship wholesale orders across the U.S., Canada, and Mexico."
            }
        },
        {
            "@type": "Question",
            "name": "Who pays for shipping on wholesale orders?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Wholesale customers cover all shipping costs. Free shipping promotions do not apply to wholesale orders."
            }
        }
    ]
};

const offerCatalogJsonLd = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": "https://brownedandtoasted.com/wholesale#catalog",
    "name": "Wholesale Marshmallow Crispy Bars",
    "itemListElement": [
        {
            "@type": "OfferCatalog",
            "name": "Case Packs",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                        "@type": "Product",
                        "name": "Marshmallow Crispy Bars — Wholesale 12-Pack",
                        "brand": { "@type": "Brand", "name": "Browned & Toasted" },
                        "category": "Dessert",
                        "offers": {
                            "@type": "Offer",
                            "availability": "https://schema.org/InStock",
                            "priceCurrency": "USD",
                            "eligibleRegion": ["US", "CA", "MX"],
                            "seller": { "@id": "https://brownedandtoasted.com#organization" }
                        }
                    }
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                        "@type": "Product",
                        "name": "Marshmallow Crispy Bars — Wholesale 24-Pack",
                        "brand": { "@type": "Brand", "name": "Browned & Toasted" },
                        "category": "Dessert",
                        "offers": {
                            "@type": "Offer",
                            "availability": "https://schema.org/InStock",
                            "priceCurrency": "USD",
                            "eligibleRegion": ["US", "CA", "MX"],
                            "seller": { "@id": "https://brownedandtoasted.com#organization" }
                        }
                    }
                }
            ]
        }
    ]
};

const breadcrumbsJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brownedandtoasted.com/" },
        { "@type": "ListItem", "position": 2, "name": "Wholesale", "item": "https://brownedandtoasted.com/wholesale" }
    ]
};

export async function getStaticProps(context) {
    const { params, locale, locales, defaultLocale } = context
    const globalData = await getGlobalData(locale)

    const pageContext = {
        locale,
        locales,
        defaultLocale,
        //      localizations,
    }

    let metadata = {
        metaTitle: `Browned & Toasted | Wholesale Gourmet Crispy Treats`,
        metaDescription: `Learn how to stock your store with Browned & Toasted's gourmet crispy treats.`,
        canonicalURL: `https://brownedandtoasted.com/wholesale`,
    }

    let preview = context.preview || false
    //  const localizedPaths = getLocalizedPaths(pageContext)

    // fetch the available bars
    try {
        return (
            {
                props: {
                    globalData,
                    metadata,
                    pageContext: pageContext,
                    locale,
                    preview: preview,
                },
            }
        )
    } catch (error) {
        return {
            revalidate: 60,
            props: {
                locale,
                error: 500
            }
        }
    }
}


export default WholesalePage